import { createStore } from 'vuex';
import { Auth, API, registerApiEndpoint } from '@/utils/amplifyCompat';
import { getApiKey } from '@/utils/apiKeys';
import moment from 'moment';
import createPersistedState from 'vuex-persistedstate';
import { Modal } from 'bootstrap';

export default createStore({
  plugins: [
    createPersistedState({
      paths: [
        'envelopeId',
        'agreementId',
        'addedUser',
        'selected_session',
        'selected_avatar_url',
        'selected_avatar_id',
        'choosedAvatarIndex',
        'pod_checkin',
        'selected_site_id',
        'selectedSiteForScheduler',
        'waiverData',
        'currentSelectedType',
        'calendarSelectedDate',
        'profile',
        'role',
        'viewFromMenu',
      ],
    }),
  ],
  state: {
    currentSelectedType: 'week',
    calendarSelectedDate: null,
    view: '',
    component: '',
    role: '',
    theme: '',
    podSessionDate: '',
    showCalModal: true,
    refresh: false,
    appointmentsRefresh: true,
    whiteListUsers: [],
    whiteListUserData: [],
    selectedUserRole: null,
    calDates: null,
    userFromAllSites: {},

    // Instructor
    calendarSessionOrders: [],
    batchTemplateMultiSelect: [],
    templates: [],
    sessionsCalendar: [],
    calendarSelectedPod: '',
    calendarSelectedGearup: {
      gearup_id: 'ASU00004-03-A',
      max_users: 4,
      pod_id: 'ASU00004-03',
      install_date: '2024-02-20',
      gearup_letter: 'A',
      status_code: 1100,
      in_use: false,
      status_description: 'active',
    },
    calendarEvents: [],

    calendarPodOneEvents: [],
    calendarPodTwoEvents: [],
    calendarPodThreeEvents: [],
    calendarPodFourEvents: [],
    whiteListedUserData: [],
    // v2 pagination state — per-site cursors so Next/Prev work across all 3 locations
    usersV2Pagination: {
      hasNextPage: false,
      nextCursors: {}, // { ASU00002: lastUserId, ... } — cursor to use for the next page
      cursorHistory: [], // stack of 'after' params used to load each previous page
      currentAfter: {}, // 'after' params used to load the current page
    },
    editTemplateData: {},

    // commom
    profile: {},
    avatars: [],
    sites: [],
    titles: [],
    semesters: [],
    sessions: [],
    scheduler_pods: [],
    titleid: 'alienzoolearnm01r02',
    loader: false,

    // student
    expirenceTIckets: [], // tree
    expierenceAvailabilities: {}, // select pod expierence modal
    selected_title_id: 'alienzoolearnm01r02', // selected title_id
    selected_title: '',
    selected_site_id: 'ASU00004', // selected site_id
    selected_site_location: '',
    reserved_spot: {}, // selected reserved spot details from pod session
    order_transaction: {}, // response from order POST
    remote_expierence: {}, // remote expierenece
    studentOrders: [], // orders of a signed in
    orderCarousel: true,
    adaSelection: false,
    selectedOrder: {},
    choosedAvatarIndex: 0,
    pod_checkin: false,
    error: {},
    waiverData: {},

    // pod operator
    selected_session: '', // selected session while adding user
    selected_avatar_id: 'alienzoo01-01', // selected Avatar
    selected_avatar_url: 'https://d11dbhgooo73xe.cloudfront.net/alienzoo01/01/fe-as-01.png',
    SchedulerSessions: [], // in pod operator get available sessions to get orders wrt them
    crossSystemEvents: [], // cross-system occupancy events from other DSL systems
    orders: [],
    addedUser: {},
    CancelReservation: null,
    cancelSession: null,

    // SSO
    user: {},
    // profile: {},
    dslApiEnabled: false,

    // Waicer
    docusignEvent: undefined,
    waivers: [],
    envelopeId: undefined,
    agreementId: undefined,

    // On click menu options
    viewFromMenu: 'Dashboard',
    allSites: [],
    accessibleSiteIds: null, // populated after profile validation; null = not yet validated
    selectedSiteForScheduler: {},
    allCourses: [],
    selectedCourseForScheduler: {},
  },
  getters: {
    getSelectedDate(state) {
      return state.podSessionDate;
    },
    getSchedulerPods(state) {
      return state.scheduler_pods;
    },

    getUser: (state) => state.user,
    signedIn: (state) => Object.keys(state.user).length !== 0,
    getProfile: (state) => state.profile,
    dslApiEnabled: (state) => state.dslApiEnabled,

    getWaivers: (state) => state.waivers,
    getWaiverEnvelopeId: (state) => state.envelopeId,
    getDocusignEvent: (state) => state.docusignEvent,
    getSchedulerSessions: (state) => state.SchedulerSessions,
    getWhitelistUsers: (state) => state.whiteListUsers,
    getWhiteListUserData: (state) => state.whiteListUserData,
    getUsersV2Pagination: (state) => state.usersV2Pagination,
  },

  mutations: {
    setCurrentSelectedType(state, selectedType) {
      state.currentSelectedType = selectedType;
    },
    setCalendarSelectedDate(state, date) {
      state.calendarSelectedDate = date;
    },
    CLEAR_USER_DATA(state) {
      state.whiteListUserData = [];
      state.whiteListUsers = [];
      state.selectedUserRole = null;
    },
    SET_MYWAIVER(state, data) {
      state.waiverData = data;
    },
    POD_CHECKIN(state, data) {
      state.pod_checkin = data;
    },
    SET_SELECTED_SITE(state, site) {
      state.selectedSiteForScheduler = site;
      state.selected_site_id = site.site_id;
    },
    SET_ALLSITES(state, data) {
      state.allSites = state.accessibleSiteIds
        ? data.filter((s) => state.accessibleSiteIds.includes(s.site_id))
        : data;

      const persistedSiteId = state.selectedSiteForScheduler?.site_id;
      const isAccessible =
        persistedSiteId && state.allSites.some((s) => s.site_id === persistedSiteId);
      const isOutOfSync = persistedSiteId && persistedSiteId !== state.selected_site_id;

      if (!isAccessible || isOutOfSync) {
        // No valid persisted selection — default to Tempe (ASU00004) if available,
        // otherwise fall back to the first accessible site.
        // Note: admin-first defaulting is handled at login time in LoggedIn.vue.
        const tempe = state.allSites.find((s) => s.site_id === 'ASU00004');
        state.selectedSiteForScheduler = tempe || state.allSites[0];
      } else {
        // Valid persisted site — enrich with full site object from API data.
        state.selectedSiteForScheduler = state.allSites.find((s) => s.site_id === persistedSiteId);
      }

      if (state.selectedSiteForScheduler?.site_id) {
        state.selected_site_id = state.selectedSiteForScheduler.site_id;
      }
    },
    SET_ACCESSIBLE_SITES(state, { sites, defaultSite }) {
      state.accessibleSiteIds = sites.map((s) => s.site_id);
      state.allSites = sites;
      state.selectedSiteForScheduler = defaultSite;
      state.selected_site_id = defaultSite.site_id;
    },
    SET_SESSIONS(state, data) {
      state.sessionsCalendar = data;
    },
    SET_PROFILE(state, data) {
      state.profile = data;
    },
    SET_GENERAL_SESSIONS(state, data) {
      state.sessions = data;
    },
    SET_AVATARS_GLOBAL(state, data) {
      state.avatars = data;
    },
    SET_AVATARS(state, data) {
      state.avatars = data;
    },
    SET_SITES(state, data) {
      state.sites = data;
    },
    SET_SEMESTERS(state, data) {
      state.semesters = data;
    },
    STUDENT_EXPIERENCES(state, data) {
      state.expirenceTIckets = data;
    },
    SET_EXPIERENCE_AVAILABLE(state, data) {
      state.expierenceAvailabilities = data;
    },
    RESET_EXPIERENCE_AVAILABLE(state) {
      state.expierenceAvailabilities = {};
    },
    SET_ROLE(state, role) {
      state.role = role;
    },
    SET_VIEW_FROM_MENU(state, view) {
      state.viewFromMenu = view;
    },
    SET_APPOINTMENTS_REFRESH(state, value) {
      state.appointmentsRefresh = value;
    },
    SET_STUDENT_ORDER(state, data) {
      state.order_transaction = data;
    },
    SET_WHITELIST_USERS(state, data) {
      const sortedList = [...data].sort((a, b) => {
        return new Date(b.start_timestamp).getTime() - new Date(a.start_timestamp).getTime();
      });
      state.whiteListUsers = sortedList;
    },
    SET_WHITELIST_USERDATA(state, data) {
      state.whiteListUserData = data;
    },
    SET_SELECTED_USER_ROLE(state, role) {
      state.selectedUserRole = role;
    },
    SET_USER_FROM_ALL_SITES(state, data) {
      const response = data.response;
      const site_id = data.site_id;

      response.forEach((element) => {
        element.site_id = site_id;
      });
      state.userFromAllSites[site_id] = response;
    },
    SET_SCHEDULES(state, data) {
      state.templates = data;
    },
    SET_SCHEDULER_SESSIONS(state, data) {
      state.SchedulerSessions = data || [];
      state.loader = false;
    },
    SET_CROSS_SYSTEM_EVENTS(state, data) {
      state.crossSystemEvents = data || [];
    },
    SET_TITLES(state, data) {
      state.titles = data;
    },
    SET_USER_DETAILS(state, data) {
      state.addedUser = data;
    },
    RESET_USER_DETAILS(state, data) {
      state.addedUser = data;
      state.envelopeId = data;
      state.agreementId = data;
      state.selected_avatar_id = 'alienzoo01-01';
      state.selected_avatar_url =
        'https://d11dbhgooo73xe.cloudfront.net/alienzoo01/01/fe-as-01.png';
    },
    SET_STUDENT_ORDERS(state, data) {
      state.studentOrders = data;
      state.orderCarousel = true;
    },
    SET_SESSION_ORDERS(state, data) {
      state.calendarSessionOrders = data;
    },
    SET_POD_ONE_EVENTS(state, data) {
      state.calendarPodOneEvents = data;
    },
    SET_POD_TWO_EVENTS(state, data) {
      state.calendarPodTwoEvents = data;
    },
    SET_POD_THREE_EVENTS(state, data) {
      state.calendarPodThreeEvents = data;
    },
    SET_POD_FOUR_EVENTS(state, data) {
      state.calendarPodFourEvents = data;
    },
    SET_BATCH_TEMPLATES(state, data) {
      state.batchTemplateMultiSelect = data;
    },
    SET_WHITELIST_USER_DATA(state, data) {
      state.whiteListedUserData = data;
    },
    RESET_USERS_V2_PAGINATION(state) {
      state.usersV2Pagination = {
        hasNextPage: false,
        nextCursors: {},
        cursorHistory: [],
        currentAfter: {},
      };
    },
    SET_WHITELIST_USER_DATA_V2(
      state,
      { items, hasNextPage, direction, afterCursors, nextCursors }
    ) {
      state.whiteListedUserData = items;
      const p = state.usersV2Pagination;
      p.hasNextPage = hasNextPage;
      p.nextCursors = nextCursors;
      if (direction === 'next') {
        p.cursorHistory.push({ ...p.currentAfter }); // save current page's 'after' so Prev can return here
        p.currentAfter = { ...afterCursors };
      } else if (direction === 'prev') {
        p.cursorHistory.pop();
        p.currentAfter = { ...afterCursors };
      } else {
        p.cursorHistory = [];
        p.currentAfter = {};
      }
    },
    SET_ERROR(state, err) {
      state.error = err;
      state.envelopeId = undefined;
    },

    // SSO

    setProfile(state, payload) {
      state.profile = payload;
    },
    setUser(state, payload) {
      state.user = payload;
    },
    setDSLApiFlag(state, flag) {
      state.dslApiEnabled = flag;
    },

    setWaivers(state, payload) {
      state.waivers = payload;
    },
    setEnvelopeId(state, payload) {
      state.envelopeId = payload;
    },
    setAgreementId(state, payload) {
      state.agreementId = payload;
    },
    REFRESH(state) {
      state.refresh = true;
    },
    SET_SCHEDULER_PODS(state, pods) {
      // Use spread to create a new object reference for Vue reactivity
      state.scheduler_pods = { ...pods };
    },
  },
  actions: {
    updateCurrentSelectedType({ commit }, selectedType) {
      commit('setCurrentSelectedType', selectedType);
    },
    updateCalendarSelectedDate({ commit }, date) {
      commit('setCalendarSelectedDate', date);
    },
    async setAllSites({ commit }) {
      // Fetching all the sites and creating Amplify Configure with each site_id
      await API.get('proxy', '/common/asu/sites')
        .then((response) => {
          commit('SET_ALLSITES', response);

          // Register dynamic API endpoints for each site
          response.forEach((site) => {
            // Register asulink endpoint for this site
            registerApiEndpoint(`${site.site_id}`, {
              endpoint: process.env.VUE_APP_ASULINK_API_URL.replace('site_id', `${site.site_id}`),
              custom_header: async () => {
                return {
                  Authorization: `Bearer ${(await Auth.currentSession())
                    .getIdToken()
                    .getJwtToken()}`,
                  'x-api-key':
                    site.site_id === 'ASU00002'
                      ? process.env.VUE_APP_ASULINK_API_KEY_ASU02
                      : site.site_id === 'ASU00003'
                        ? process.env.VUE_APP_ASULINK_API_KEY_ASU03
                        : site.site_id === 'ASU00004'
                          ? process.env.VUE_APP_ASULINK_API_KEY_ASU04
                          : site.site_id === 'ASU00005'
                            ? process.env.VUE_APP_ASULINK_API_KEY_ASU05
                            : process.env.VUE_APP_ASULINK_API_KEY,
                };
              },
            });

            // Register proxy endpoint for this site
            registerApiEndpoint(`proxy-${site.site_id}`, {
              endpoint: `${process.env.VUE_APP_PROXY_API_URL}`,
              custom_header: async () => {
                return {
                  Authorization: `Bearer ${(await Auth.currentSession())
                    .getIdToken()
                    .getJwtToken()}`,
                  'x-api-key':
                    site.site_id === 'ASU00002'
                      ? process.env.VUE_APP_ASULINK_API_KEY_ASU02
                      : site.site_id === 'ASU00003'
                        ? process.env.VUE_APP_ASULINK_API_KEY_ASU03
                        : site.site_id === 'ASU00004'
                          ? process.env.VUE_APP_ASULINK_API_KEY_ASU04
                          : site.site_id === 'ASU00005'
                            ? process.env.VUE_APP_ASULINK_API_KEY_ASU05
                            : process.env.VUE_APP_ASULINK_API_KEY,
                };
              },
            });
          });

          return response;
        })
        .catch((err) => {
          return err.response;
        });
    },
    async createTemplate(context, data) {
      return await API.put('asulink', '/scheduler/schedule', data)
        .then((response) => {
          response.status = '200';
          return response;
        })
        .catch((err) => {
          return err.response;
        });
    },
    async editTemplate(context, data, id) {
      return await API.patch('asulink', `/scheduler/schedule/${id}`, data)
        .then((response) => {
          return response;
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async deleteTemplate({ dispatch }, schedule_Id) {
      return await API.del('asulink', `/scheduler/schedule/${schedule_Id}`)
        .then((response) => {
          dispatch('setListOfTemplates');
          response = true;
          return response;
        })
        .catch((err) => {
          return err.response;
        });
    },
    // Lets API errors propagate. API.put throws a plain Error with no .response, so the
    // old `.catch((err) => err.response)` resolved to undefined and the caller could only
    // ever show a generic message. The caller parses the server's message out of it.
    async addASession({ dispatch, state }, details) {
      const response = await API.put(
        `proxy-${state.selected_site_id}`,
        `/${state.selected_site_id}/dsl/scheduler/session`,
        details.addASessionBody
      );
      // The session exists at this point, so a failed refresh must not be reported as a
      // failed create.
      try {
        await dispatch('setCalendarSessionsWithStartAndEndDate', details.myData);
      } catch (err) {
        console.error('Session created but the calendar refresh failed', err);
      }
      return response;
    },
    async setSessions({ commit, state }) {
      return await API.get(
        `proxy-${state.selected_site_id}`,
        `/${state.selected_site_id}/dsl/scheduler/sessions/${state.selected_site_id}`
      )
        .then((response) => {
          commit('SET_SCHEDULER_SESSIONS', response);
          // Create a deep copy of scheduler_pods for reactivity
          const skeleton = JSON.parse(JSON.stringify(state.scheduler_pods));
          for (const pod in skeleton) {
            skeleton[pod].scheduler_sessions = [];
          }

          response.forEach((cs) => {
            const conStart = moment(cs.start_timestamp);
            const conEnd = moment(cs.end_timestamp);

            const startDT = conStart.tz('America/Phoenix').format();
            const endDT = conEnd.tz('America/Phoenix').format();
            const startDTFormating = startDT.substr(0, startDT.indexOf('-07:00'));
            const endDTFormating = endDT.substr(0, endDT.indexOf('-07:00'));

            for (const pod in skeleton) {
              if (Object.prototype.hasOwnProperty.call(skeleton, pod)) {
                const currentPod = skeleton[pod];

                if (currentPod.gear_id === cs.gearup_id) {
                  if (!skeleton[pod].scheduler_sessions) {
                    skeleton[pod].scheduler_sessions = [];
                  }
                  if (!Array.isArray(skeleton[pod].scheduler_sessions)) {
                    continue;
                  }
                  // Push the session data to the scheduler_sessions array of the current pod
                  skeleton[pod].scheduler_sessions.push({
                    name: cs.title_name,
                    start: startDTFormating,
                    end: endDTFormating,
                    color: '#151515',
                    sessionData: cs,
                  });
                }
              }
            }
          });

          // Use mutation for proper Vue reactivity
          commit('SET_SCHEDULER_PODS', skeleton);
          commit('REFRESH');
          return response;
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async getCalendarSessionOrders({ commit }, sessionId) {
      return await API.get('asulink', `/scheduler/session/${sessionId}/orders`).then((response) => {
        const orders = response;
        commit('SET_SESSION_ORDERS', orders);
      });
    },
    async setListOfTemplates({ commit }) {
      return await API.get('asulink', '/scheduler/schedules')
        .then((response) => {
          const data = [];
          response.forEach((ts) => {
            const template = ts;
            template.checked = false;
            data.push(template);
          });
          commit('SET_BATCH_TEMPLATES', data);
          commit('SET_SCHEDULES', response);
          return response;
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setProfile({ commit }) {
      return await API.get('asulink', '/account/profile')
        .then((response) => {
          const profile = response;
          commit('SET_PROFILE', profile);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setGeneralSessions({ commit }) {
      return await API.get('asulink', '/account/sessions')
        .then((response) => {
          commit('SET_GENERAL_SESSIONS', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setAvatarsGlobal({ commit, state }) {
      return await API.get('proxy', `/${state.selected_site_id}/dsl/common/avatars`)
        .then((response) => {
          commit('SET_AVATARS_GLOBAL', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },

    async setAvatars({ commit }, titleid) {
      return await API.get('asulink', `/common/avatars/${titleid}`)
        .then((response) => {
          commit('SET_AVATARS', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setSites({ commit }) {
      return await API.get('asulink', '/common/sites')
        .then((response) => {
          commit('SET_SITES', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setSemesters({ commit, state }) {
      return await API.get('proxy', `/${state.selected_site_id}/dsl/common/asu/semesters`)
        .then((response) => {
          commit('SET_SEMESTERS', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setStudentExpierences({ commit }, site_id) {
      return await API.get('asulink', `/tickets/experiences/${site_id}`)
        .then((response) => {
          commit('STUDENT_EXPIERENCES', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setExpierenceAvailability({ commit }, payload) {
      return await API.get(
        'asulink',
        `/tickets/experiences/availability/${payload.site_id}/${payload.title_id}/?ada=true&startdate=${payload.start_date}`
      )
        .then((response) => {
          commit('SET_EXPIERENCE_AVAILABLE', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
          commit('RESET_EXPIERENCE_AVAILABLE');
        });
    },

    async setStudentOrder({ commit, state }, order_details) {
      return await API.post(
        'proxy',
        `/${state.selected_site_id}/dsl/tickets/experience/order`,
        order_details
      )
        .then((response) => {
          commit('SET_STUDENT_ORDER', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    /**
     * Fetch users from v2 endpoint with server-side pagination, search, and filters.
     * @param {Object} params
     * @param {number} params.limit - Items per page
     * @param {string} params.afterUserId - Cursor for pagination
     * @param {string} params.direction - 'next', 'prev', or null (fresh load)
     * @param {string} params.search - Search term
     * @param {string} params.searchField - 'name' | 'email' | 'asurite'
     * @param {string[]} params.role - Role filter
     * @param {string[]} params.semester - Semester filter
     * @param {string[]} params.course - Course filter
     * @param {string[]} params.instructionMode - Instruction mode filter
     */
    async fetchWhiteListUsersV2({ commit, state }, params = {}) {
      const {
        search = '',
        searchField = 'name',
        role = [],
        semester = [],
        course = [],
        instructionMode = [],
        direction = null, // 'next' | 'prev' | null (fresh load / filter change)
      } = params;

      const limit = 10;
      const pagination = state.usersV2Pagination;

      // Resolve per-site `after` cursors based on navigation direction
      let afterCursors;
      if (direction === 'next') {
        afterCursors = { ...pagination.nextCursors };
      } else if (direction === 'prev') {
        const history = pagination.cursorHistory;
        afterCursors = history.length > 0 ? { ...history[history.length - 1] } : {};
      } else {
        afterCursors = {}; // fresh load — start each site from the beginning
      }

      const buildQuery = (siteId) => {
        const qp = new URLSearchParams();
        qp.set('limit', limit);
        qp.set('include_roster', 'true');
        const after = afterCursors[siteId];
        if (after) qp.set('after', after);
        if (search && search.trim()) {
          const trimmed = search.trim();
          if (searchField === 'email') {
            qp.set('email_like', trimmed);
          } else if (searchField === 'asurite') {
            qp.set('asurite_like', trimmed);
          } else {
            qp.set('name_like', trimmed);
          }
        }
        role.forEach((r) => qp.append('role', r));
        semester.forEach((s) => qp.append('semester', s));
        course.forEach((c) => qp.append('course', c));
        instructionMode.forEach((m) => qp.append('instruction_mode', m));
        return qp.toString();
      };

      const sites = [{ site_id: state.selected_site_id }];

      try {
        const results = await Promise.allSettled(
          sites.map((site) =>
            API.get('proxy', `/${site.site_id}/dsl/access/users/v2?${buildQuery(site.site_id)}`, {
              headers: { 'x-api-key': getApiKey(site.site_id) },
            })
          )
        );

        const siteResults = results.map((r, i) => ({
          siteId: sites[i].site_id,
          items: r.status === 'fulfilled' ? r.value?.items || [] : [],
        }));

        const items = siteResults.flatMap((r) => r.items);

        // Build next-page cursors from the last user_id returned per site
        const nextCursors = {};
        siteResults.forEach(({ siteId, items: siteItems }) => {
          if (siteItems.length > 0) {
            nextCursors[siteId] = siteItems[siteItems.length - 1].user_id;
          }
        });

        // There's a next page if any site returned a full page of results
        const hasNextPage = siteResults.some((r) => r.items.length >= limit);

        commit('SET_WHITELIST_USER_DATA_V2', {
          items,
          hasNextPage,
          direction,
          afterCursors,
          nextCursors,
        });
        return { items };
      } catch (err) {
        window.console.log('Error fetching users v2', err);
        throw err;
      }
    },
    async fetchUserDataFromSites({ state }, { dataTerms, urlPath }) {
      // Recursive function -> fetch url based on skip count.
      const fetchUserData = async ({ site_id, user_id }, index = 0, accumulated = []) => {
        try {
          const skipParam = index > 0 ? `&skip=${index}` : '';
          const url = `${urlPath}${user_id}?include_partial=true${skipParam}`;
          const response = await API.get(`${site_id}`, url);

          const _allResponse = [...accumulated, ...response];

          if (response.length === 25) {
            return await fetchUserData({ site_id, user_id }, index + 1, _allResponse);
          }

          return _allResponse;
        } catch (error) {
          return accumulated;
        }
      };

      const allResults = [];
      // Map of site_id -> matchingUser object (includes role per site)
      const siteUsers = {};

      // If site_id and userId are provided directly, use them
      if (dataTerms.site_id && dataTerms.userId) {
        const _data = await fetchUserData({
          site_id: dataTerms.site_id,
          user_id: dataTerms.userId,
        });
        if (_data && _data.length > 0) {
          allResults.push(..._data);
        }
      } else {
        // Fallback: look up user by email via v2 endpoint, then fetch their data
        await Promise.all(
          state.allSites.map(async (site) => {
            try {
              const result = await API.get(
                'proxy',
                `/${site.site_id}/dsl/access/users/v2?email_like=${encodeURIComponent(dataTerms.email)}&limit=10`,
                { headers: { 'x-api-key': getApiKey(site.site_id) } }
              );
              const matchingUser = (result?.items || []).find((u) => u.email === dataTerms.email);
              if (!matchingUser) return;

              // Track the per-site user profile so callers can read the correct role
              siteUsers[site.site_id] = matchingUser;

              const _data = await fetchUserData({
                site_id: site.site_id,
                user_id: matchingUser.user_id,
              });

              if (_data && _data.length > 0) {
                allResults.push(..._data);
              }
            } catch (e) {
              // ignore individual site failures
            }
          })
        );
      }

      return { results: allResults, siteUsers };
    },
    async getWhiteListUserData({ commit, dispatch, state }, dataTerms) {
      // Pass only email so fetchUserDataFromSites looks up the correct user_id
      // per site and fetches sessions from ALL locations
      const { results, siteUsers } = await dispatch('fetchUserDataFromSites', {
        dataTerms: { email: dataTerms.email },
        urlPath: '/access/sessions/',
      });
      commit('SET_WHITELIST_USERS', results);

      // Use the role returned for the currently selected location so the modal
      // always reflects the right role regardless of how many sites were queried.
      const selectedSiteUser = siteUsers[state.selected_site_id];
      if (selectedSiteUser?.role) {
        commit('SET_SELECTED_USER_ROLE', selectedSiteUser.role);
      }
    },
    async getUserData({ commit, dispatch }, dataTerms) {
      // Pass only email so fetchUserDataFromSites looks up the correct user_id
      // per site and fetches user data from ALL locations
      const { results } = await dispatch('fetchUserDataFromSites', {
        dataTerms: { email: dataTerms.email },
        urlPath: '/access/userdata/',
      });
      commit('SET_WHITELIST_USERDATA', results);
    },

    /**
     * Fetch cross-system occupancy events from the proxy API.
     * These are shows running on OTHER systems (e.g. VS) that occupy pods
     * and should be displayed as non-interactive stripe-filled blocks on
     * the Pod Schedule and Scheduler pages.
     *
     * Endpoint: GET /{site_id}/dsl/scheduler/cross-system-occupancy
     *   ?starttime=YYYY-MM-DDT00:00:00-07:00
     *   &endtime=YYYY-MM-DDT23:59:59-07:00
     *
     * Uses the same Amplify proxy transport as setSchedulerSessions.
     *
     * @param {Object} myData
     * @param {string} myData.selected_site_id - Active site id (e.g. 'ASU00004')
     * @param {string} myData.startDate        - 'YYYY-MM-DD' start of range
     * @param {string} myData.endDate          - 'YYYY-MM-DD' end of range
     */
    async fetchCrossSystemOccupancy({ commit }, myData) {
      const { selected_site_id, startDate, endDate } = myData;
      if (!selected_site_id || !startDate || !endDate) return;
      try {
        console.debug('[cross-system-occupancy] GET', selected_site_id, startDate, '→', endDate);
        const response = await API.get(
          `proxy-${selected_site_id}`,
          `/${selected_site_id}/dsl/scheduler/cross-system-occupancy?site_id=${selected_site_id}&start_at=${startDate}T00:00:00-07:00&end_at=${endDate}T23:59:59-07:00`
        );
        const events = Array.isArray(response) ? response : response?.events || [];
        const normalised = events.map((ev) => {
          // Convert to Phoenix local time then strip the offset, matching the same
          // naive-ISO format that setSchedulerSessions uses for FullCalendar events.
          // e.g. "2026-08-11T16:30:00-07:00" → "2026-08-11T16:30:00"
          const startPhoenix = moment(ev.start_at || ev.start_timestamp || ev.start_time).tz(
            'America/Phoenix'
          );
          const endPhoenix = moment(ev.end_at || ev.end_timestamp || ev.end_time).tz(
            'America/Phoenix'
          );
          const startFormatted = startPhoenix.format();
          const endFormatted = endPhoenix.format();
          return {
            ...ev,
            // Naive ISO (no offset) for FullCalendar — consistent with regular session events
            start_timestamp: startFormatted.replace(/-07:00$/, '').replace(/\+00:00$/, ''),
            end_timestamp: endFormatted.replace(/-07:00$/, '').replace(/\+00:00$/, ''),
          };
        });
        commit('SET_CROSS_SYSTEM_EVENTS', normalised);
        return normalised;
      } catch (err) {
        // Non-fatal — leave existing data in place rather than clearing on a failed refetch
        console.warn('fetchCrossSystemOccupancy failed:', err);
      }
    },
    async setSchedulerSessions({ commit, state }, myData) {
      return await API.get(
        `proxy-${myData.selected_site_id}`,
        `/${myData.selected_site_id}/dsl/scheduler/sessions/${myData.selected_site_id}?starttime=${
          myData.startDate ? myData.startDate : myData.podSessionDate
        }T00:00:00-07:00&endtime=${
          myData.end_time ? myData.end_time : myData.podSessionDate
        }T23:59:59-07:00&visibility=all&limit=1500`
      )
        .then((response) => {
          response.forEach((cs) => {
            const conStart = moment(cs.start_timestamp);
            const conEnd = moment(cs.end_timestamp);

            const startDT = conStart.tz('America/Phoenix').format();
            const endDT = conEnd.tz('America/Phoenix').format();
            cs.start_timestamp = startDT;
            cs.end_timestamp = endDT;
          });
          commit('SET_SCHEDULER_SESSIONS', response);
          // Create a deep copy for Vue reactivity
          const skeleton = JSON.parse(JSON.stringify(state.scheduler_pods));
          for (const pod in skeleton) {
            skeleton[pod].scheduler_sessions = [];
          }
          response.forEach((cs) => {
            const conStart = moment(cs.start_timestamp);
            const conEnd = moment(cs.end_timestamp);

            const startDT = conStart.tz('America/Phoenix').format();
            const endDT = conEnd.tz('America/Phoenix').format();
            const startDTFormating = startDT.substr(0, startDT.indexOf('-07:00'));
            const endDTFormating = endDT.substr(0, endDT.indexOf('-07:00'));

            for (const pod in skeleton) {
              if (Object.prototype.hasOwnProperty.call(skeleton, pod)) {
                const currentPod = skeleton[pod];
                if (currentPod.gear_id === cs.gearup_id) {
                  if (!skeleton[pod].scheduler_sessions) {
                    skeleton[pod].scheduler_sessions = [];
                  }
                  if (!Array.isArray(skeleton[pod].scheduler_sessions)) {
                    continue;
                  }

                  // Push the session data to the scheduler_sessions array of the current pod
                  if (
                    !skeleton[pod].scheduler_sessions.some(
                      (item) => item.sessionData.session_id === cs.session_id
                    )
                  ) {
                    skeleton[pod].scheduler_sessions.push({
                      name: cs.title_name,
                      start: startDTFormating,
                      end: endDTFormating,
                      color: '#151515',
                      sessionData: cs,
                    });
                  }
                }
              }
            }
          });

          // Use mutation for proper Vue reactivity
          commit('SET_SCHEDULER_PODS', skeleton);
          commit('REFRESH');
          return response;
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setCalendarSessionsWithStartAndEndDate({ commit, state }, myData) {
      state.refresh = false;
      return await API.get(
        `proxy-${myData.selected_site_id}`,
        `/${myData.selected_site_id}/dsl/scheduler/sessions/${myData.selected_site_id}?starttime=${myData.start_time}T00:00:00-07:00&endtime=${myData.end_time}T23:59:59-07:00&visibility=all&limit=1500`
      )
        .then((response) => {
          const sessions = response || [];
          commit('SET_SCHEDULER_SESSIONS', sessions);
          // Create a deep copy of scheduler_pods for reactivity
          const skeleton = JSON.parse(JSON.stringify(state.scheduler_pods));
          for (const pod in skeleton) {
            skeleton[pod].scheduler_sessions = [];
          }
          sessions.forEach((cs) => {
            const conStart = moment(cs.start_timestamp);
            const conEnd = moment(cs.end_timestamp);

            const startDT = conStart.tz('America/Phoenix').format();
            const endDT = conEnd.tz('America/Phoenix').format();
            const startDTFormating = startDT.substr(0, startDT.indexOf('-07:00'));
            const endDTFormating = endDT.substr(0, endDT.indexOf('-07:00'));

            for (const pod in skeleton) {
              if (Object.prototype.hasOwnProperty.call(skeleton, pod)) {
                const currentPod = skeleton[pod];
                if (currentPod.gear_id === cs.gearup_id) {
                  if (!skeleton[pod].scheduler_sessions) {
                    skeleton[pod].scheduler_sessions = [];
                  }
                  if (!Array.isArray(skeleton[pod].scheduler_sessions)) {
                    continue;
                  }

                  // Push the session data to the scheduler_sessions array of the current pod
                  if (
                    !skeleton[pod].scheduler_sessions.some(
                      (item) => item.sessionData.session_id === cs.session_id
                    )
                  ) {
                    skeleton[pod].scheduler_sessions.push({
                      name: cs.title_name,
                      start: startDTFormating,
                      end: endDTFormating,
                      color: '#151515',
                      sessionData: cs,
                    });
                  }
                }
              }
            }
          });

          // Use mutation for proper Vue reactivity
          commit('SET_SCHEDULER_PODS', skeleton);
          commit('REFRESH');
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async setTitles({ commit }) {
      await API.get('asulink', '/common/titles')
        .then((response) => {
          commit('SET_TITLES', response);
          // return response;
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    async sendUserDetails({ commit }, user_details) {
      return await API.post('asulink', '/customer/adhoc/checkin/identify/guest', user_details)
        .then((response) => {
          commit('SET_USER_DETAILS', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },
    resetUserDetails({ commit }) {
      commit('RESET_USER_DETAILS', undefined);
    },
    async confirmUserDetails({ commit }, user_details) {
      return await API.post('asulink', '/customer/adhoc/checkin/complete', user_details)
        .then(async (response) => {
          const tempData = {
            body: {
              first_name: this.state.waiverData.body.first_name,
              last_name: this.state.waiverData.body.last_name,
              email: this.state.waiverData.body.email,
              site_id: this.state.waiverData.body.site_id,
              signing_init_timestamp: response.user_session.checked_in_timestamp,
            },
          };

          await API.put('proxy', `/${this.state.selected_site_id}/waiver/sign/data`, tempData);
          const checkinModalEl = document.getElementById('checkin-modal');
          if (checkinModalEl) {
            const modal = Modal.getOrCreateInstance(checkinModalEl);
            modal.show();
          }
          commit('RESET_USER_DETAILS', undefined);
          commit('REFRESH');
        })
        .catch((err) => {
          const errorModalEl = document.getElementById('error-checkin');
          if (errorModalEl) {
            const modal = Modal.getOrCreateInstance(errorModalEl);
            modal.show();
          }
          commit('RESET_USER_DETAILS', undefined);
          commit('SET_ERROR', err.response.data);
          window.console.log('Error occured', err.response.data.message);
        });
    },
    async resendEmail({ state }, user_details) {
      return await API.post(
        'proxy',
        `/${state.selected_site_id}/dsl/access/user/registration/email`,
        user_details
      )
        .then((response) => {
          return response;
        })
        .catch((err) => {
          window.console.log('Error occured', err.response.data.errors[0].message);
          return err.response.data.errors[0].message;
        });
    },
    // Errors are re-thrown, not swallowed. Callers show a success dialog when the
    // promise resolves, so catching here reported "Show successfully cancelled" for a
    // session that still existed, and made their own .catch unreachable dead code.
    async deleteCalendarSession({ state }, data) {
      return await API.del(
        `proxy-${state.selectedSiteForScheduler.site_id}`,
        `/${state.selectedSiteForScheduler.site_id}/dsl/scheduler/session/${data.session_id}`
      ).catch((err) => {
        window.console.log('Error occured', err);
        throw err;
      });
    },
    async deleteCalendarSessionOrder({ state }, data) {
      return await API.del(
        `proxy-${state.selected_site_id}`,
        `/${state.selected_site_id}/dsl/scheduler/session/${data.sessionId}/order/${data.orderId}`
      ).catch((err) => {
        window.console.log('Error occured', err);
        throw err;
      });
    },
    async deleteAllSessionOrders(context, data) {
      return await API.delete('asulink', `/scheduler/session/${data.sessionid}/orders`).catch(
        (err) => {
          window.console.log('Error occured', err);
        }
      );
    },
    async deleteCalendarSchedule(context, data) {
      return await API.delete('asulink', `/scheduler/schedule/${data.scheduleid}`).catch((err) => {
        window.console.log('Error occured', err);
      });
    },
    async getOutstandingOrders({ commit }) {
      return await API.get('asulink', '/tickets/orders')
        .then((response) => {
          commit('SET_STUDENT_ORDERS', response);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
        });
    },

    // SSO
    async loadUser({ commit }) {
      try {
        const response = await Auth.currentAuthenticatedUser();
        commit('setUser', response);
      } catch (error) {
        commit('setUser', {});
      }
    },
    async clearUserState({ commit }) {
      commit('setUser', {});
      commit('setProfile', {});
    },
    async getDslAccountProfile({ commit }) {
      if (this.state.dslApiEnabled) {
        const response = await API.get('asulink', '/account/profile');
        commit('setProfile', response);
      }
    },

    // Waiver
    async resetWaiverState({ dispatch, commit }) {
      await dispatch('loadWaivers');
      commit('setEnvelopeId', undefined);
      commit('setAgreementId', undefined);
      commit('setDocusignEvent', undefined);
    },
    async loadDslAccountProfile({ commit }) {
      if (this.state.dslApiEnabled) {
        const response = await API.get('asulink', '/account/profile');
        commit('setProfile', response);
      }
    },
    async loadWaivers({ commit }) {
      if (this.state.dslApiEnabled) {
        const response = await API.get('asulink', '/account/waivers/valid');
        commit('setWaivers', response);
      }
    },
  },
  modules: {},
});
